import React, { useState, useEffect, useCallback } from "react";
import { Search, RefreshCw, Eye, Trash2, Calendar } from "lucide-react";
import { Add } from "iconsax-reactjs";
import apiService from "../../services/api";
import CreateOpportunityModal from "../../components/CreateOpportunityModal";
import { useNavigate } from "react-router-dom";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";

const MyOpportunities = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  console.log("i", isDarkMode);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    salesStatus: "",
    search: "",
    sortBy: "expectedCloseDate",
    sortOrder: "ASC",
  });
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleRealtimeUpdate = useCallback((data) => {
    console.log("📡 Received real-time opportunity update:", data);

    if (data.type === "opportunity_updated") {
      const { action, data: opportunityData } = data;

      setOpportunities((prevOpportunities) => {
        if (action === "created") {
          return [opportunityData, ...prevOpportunities];
        } else if (
          action === "updated" ||
          action === "sold" ||
          action === "stage_changed"
        ) {
          return prevOpportunities.map((opp) =>
            opp.id === opportunityData.id
              ? { ...opp, ...opportunityData }
              : opp,
          );
        } else if (action === "deleted") {
          return prevOpportunities.filter(
            (opp) => opp.id !== opportunityData.id,
          );
        }
        return prevOpportunities;
      });
    }
  }, []);

  const { isConnected } = useRealtimeSync({
    events: ["opportunity_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  useEffect(() => {
    fetchOpportunities();
  }, [pagination.page, filters]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const response = await apiService.getMyOpportunities({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });

      setOpportunities(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    fetchOpportunities();
    setShowCreateModal(false);
  };

  const handleDeleteOpportunity = async (opportunityId) => {
    if (window.confirm("Are you sure you want to delete this opportunity?")) {
      try {
        await apiService.deleteOpportunity(opportunityId);
        fetchOpportunities();
      } catch (error) {
        console.error("Error deleting opportunity:", error);
      }
    }
  };

  const getSalesStatusColor = (salesStatus) => {
    const colors = {
      none: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      appointment_booked: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      not_sat: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      sat_non_qual: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      sat_qual: "text-cyan-400 bg-cyan-400/10 border-cyan-400/20",
      sold: "text-green-400 bg-green-400/10 border-green-400/20",
      dead: "text-red-400 bg-red-400/10 border-red-400/20",
      cancelled: "text-gray-400 bg-gray-400/10 border-gray-400/20",
    };
    return (
      colors[salesStatus] || "text-gray-400 bg-gray-400/10 border-gray-400/20"
    );
  };

  const formatSalesStatus = (salesStatus) => {
    const labels = {
      none: "None",
      appointment_booked: "Appointment Booked",
      not_sat: "NOT SAT",
      sat_non_qual: "SAT NON QUAL",
      sat_qual: "SAT QUAL",
      sold: "SOLD",
      dead: "Dead",
      cancelled: "Cancelled",
    };
    return labels[salesStatus] || salesStatus;
  };

  const OpportunityRow = ({ opportunity }) => {
    const assignedUser = opportunity.assignedUser;
    const accountName =
      opportunity.account?.name || opportunity.lead?.companyName || "—";

    const initials = assignedUser?.name
      ? assignedUser.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";

    return (
      <tr
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
      >
        <td className="py-4 px-4">
          <button
            onClick={() =>
              window.open(`/opportunities/${opportunity.id}`, "_blank")
            }
            className={`font-medium hover:text-blue-400 transition-colors text-left ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {opportunity.name}
          </button>
          {accountName !== "—" && (
            <div
              className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {accountName}
            </div>
          )}
        </td>
        <td className="py-4 px-4">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium ${getSalesStatusColor(
              opportunity.salesStatus,
            )}`}
          >
            {formatSalesStatus(opportunity.salesStatus)}
          </span>
        </td>
        <td className="py-4 px-4">
          {assignedUser ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
              >
                {assignedUser.profileImageUrl ? (
                  <img
                    src={assignedUser.profileImageUrl}
                    alt={assignedUser.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {assignedUser.name}
              </span>
            </div>
          ) : (
            <span
              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Unassigned
            </span>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          {opportunity.expectedCloseDate
            ? new Date(opportunity.expectedCloseDate).toLocaleDateString()
            : "Not set"}
        </td>
        <td className="py-4 px-4 text-right">
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() =>
                window.open(`/opportunities/${opportunity.id}`, "_blank")
              }
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
              title="View details"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteOpportunity(opportunity.id);
              }}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              My Opportunities
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Opportunities assigned to you
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchOpportunities}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Opportunity</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search opportunities..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className={`pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
              />
              <Search
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>

            <select
              value={filters.salesStatus}
              onChange={(e) =>
                setFilters({ ...filters, salesStatus: e.target.value })
              }
              className={`px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
            >
              <option value="">All Stages</option>
              <option value="appointment_booked">Appointment Booked</option>
              <option value="not_sat">NOT SAT</option>
              <option value="sat_non_qual">SAT NON QUAL</option>
              <option value="sat_qual">SAT QUAL</option>
              <option value="sold">SOLD</option>
              <option value="dead">Dead</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                All Opportunities{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} opportunities
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Manage your assigned opportunities and track progress.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Opportunity
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Stage
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Assigned To
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Close Date
                  </th>
                  <th
                    className={`text-right py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="5"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading opportunities...
                    </td>
                  </tr>
                ) : opportunities.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No opportunities found. Create your first opportunity to
                      get started.
                    </td>
                  </tr>
                ) : (
                  opportunities.map((opportunity) => (
                    <OpportunityRow
                      key={opportunity.id}
                      opportunity={opportunity}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateOpportunityModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
};

export default MyOpportunities;
